import React, { useState } from "react";
import {
  Alert, Box, Button, Grid, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  useGetWhatsappSettingsQuery, useSendTestWhatsappMutation,
  useGetNumbersQuery, useUpdateNumberMutation,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, DataTable, StatusBadge, CardSurface, FormCard,
  ActionButton, SlidePanel, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const WhatsAppSettings: React.FC = () => {
  const { data: settingsData, isLoading } = useGetWhatsappSettingsQuery();
  const { data: numbersData } = useGetNumbersQuery();
  const [updateNumber] = useUpdateNumberMutation();
  const [sendTest, { isLoading: sending }] = useSendTestWhatsappMutation();

  const s = settingsData?.data;
  const numbers = numbersData?.data ?? [];

  const [enableForm, setEnableForm] = useState<any>({
    number_id: "", whatsapp_business_account_id: "", whatsapp_phone_number_id: "", whatsapp_provider: "telnyx",
  });
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState<any>({ number_id: "", to: "", message: "INTALK WhatsApp test ✅" });
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const enableWhatsApp = async () => {
    if (!enableForm.number_id) return;
    await updateNumber({
      id: Number(enableForm.number_id),
      body: {
        whatsapp_enabled: true,
        whatsapp_provider: enableForm.whatsapp_provider,
        whatsapp_business_account_id: enableForm.whatsapp_business_account_id || null,
        whatsapp_phone_number_id: enableForm.whatsapp_phone_number_id || null,
      },
    });
    setEnableForm({ number_id: "", whatsapp_business_account_id: "", whatsapp_phone_number_id: "", whatsapp_provider: "telnyx" });
  };

  const runTest = async () => {
    setResult(null);
    try {
      const res: any = await sendTest({
        number_id: testForm.number_id || undefined, to: testForm.to, message: testForm.message,
      }).unwrap();
      setResult({ ok: !!res.status, msg: res.message || "Sent" });
    } catch (e: any) {
      setResult({ ok: false, msg: e?.data?.message || "Failed to send" });
    }
  };

  const connectedColumns: DataTableColumn<any>[] = [
    { id: "number", label: "Number", render: (n) => n.number || "—" },
    { id: "whatsapp_provider", label: "Provider", render: (n) => n.whatsapp_provider || "—" },
    {
      id: "whatsapp_business_account_id", label: "WABA ID",
      render: (n) => (
        <Typography fontSize={13} color="text.secondary">
          {n.whatsapp_business_account_id || "—"}
        </Typography>
      ),
    },
    {
      id: "whatsapp_phone_number_id", label: "Phone Number ID",
      render: (n) => (
        <Typography fontSize={13} color="text.secondary">
          {n.whatsapp_phone_number_id || "—"}
        </Typography>
      ),
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<WhatsAppIcon />}
          title="WhatsApp Business"
          subtitle="Meta Business account connections and provider settings"
          actions={
            <ActionButton
              startIcon={<SendRoundedIcon />}
              onClick={() => { setResult(null); setTestOpen(true); }}
            >
              Send Test WhatsApp
            </ActionButton>
          }
        />
      }
    >
      <Stack spacing={3}>
        {/* Info banner */}
        <CardSurface noHover sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <InfoOutlinedIcon sx={{ color: NAVY, opacity: 0.6, mt: 0.25, flexShrink: 0 }} />
          <Typography fontSize={13} color="text.secondary">
            Each company connects its <strong>own Meta Business account</strong> through Telnyx WhatsApp.
            INTALK stores only the encrypted Telnyx key and the number&apos;s WhatsApp IDs.
          </Typography>
        </CardSurface>

        {/* Status cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormCard
              title="Business Status"
              subtitle="Meta Business connection state"
              icon={<WhatsAppIcon />}
            >
              {isLoading ? (
                <Typography color="text.secondary">Loading…</Typography>
              ) : (
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 140 }}>
                      Connection:
                    </Typography>
                    <StatusBadge status={s?.business_status ?? "unknown"} />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 140 }}>
                      Driver:
                    </Typography>
                    <Typography fontSize={13} fontWeight={700} color={NAVY}>{s?.driver || "—"}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 140 }}>
                      Provider configured:
                    </Typography>
                    <StatusBadge
                      status={s?.provider_configured ? "yes" : "no"}
                      tone={s?.provider_configured ? "success" : "error"}
                    />
                  </Box>
                </Stack>
              )}
            </FormCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormCard
              title="Webhook"
              subtitle="Inbound event endpoint"
              icon={<LinkRoundedIcon />}
            >
              <Stack spacing={1.5}>
                <Box>
                  <Typography fontSize={12} color="text.secondary" mb={0.5}>Webhook URL</Typography>
                  <Typography
                    fontSize={12}
                    fontFamily="monospace"
                    sx={{
                      wordBreak: "break-all",
                      bgcolor: "action.hover",
                      borderRadius: "6px",
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    {s?.webhook_url || "—"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography fontSize={13} color="text.secondary" sx={{ minWidth: 160 }}>
                    Signature verification:
                  </Typography>
                  <StatusBadge
                    status={s?.webhook_signature_enabled ? "enabled" : "disabled (dev)"}
                    tone={s?.webhook_signature_enabled ? "success" : "warning"}
                  />
                </Box>
              </Stack>
            </FormCard>
          </Grid>
        </Grid>

        {/* Connected numbers table */}
        <FormCard
          title="Connected Numbers"
          subtitle="Numbers with WhatsApp Business enabled"
          icon={<PhoneRoundedIcon />}
        >
          <DataTable
            columns={connectedColumns}
            rows={s?.connected_numbers ?? []}
            empty={{
              icon: <PhoneRoundedIcon />,
              title: "No WhatsApp numbers connected yet",
              message: "Use the form below to enable WhatsApp on a number.",
            }}
          />
        </FormCard>

        {/* Enable on a number */}
        <FormCard
          title="Enable WhatsApp on a Number"
          subtitle="Associate a Meta WABA with an existing INTALK number"
          icon={<AddRoundedIcon />}
        >
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap">
              <TextField
                select label="Number" value={enableForm.number_id}
                sx={{ minWidth: 200 }}
                onChange={(e) => setEnableForm({ ...enableForm, number_id: e.target.value })}
              >
                <MenuItem value="">Select…</MenuItem>
                {numbers.map((n: any) => (
                  <MenuItem key={n.id} value={n.id}>{n.number}</MenuItem>
                ))}
              </TextField>
              <TextField
                select label="Provider" value={enableForm.whatsapp_provider}
                sx={{ width: 160 }}
                onChange={(e) => setEnableForm({ ...enableForm, whatsapp_provider: e.target.value })}
              >
                <MenuItem value="telnyx">Telnyx</MenuItem>
                <MenuItem value="meta">Meta (future)</MenuItem>
              </TextField>
              <TextField
                label="WhatsApp Business Account ID"
                value={enableForm.whatsapp_business_account_id}
                onChange={(e) => setEnableForm({ ...enableForm, whatsapp_business_account_id: e.target.value })}
                sx={{ minWidth: 220 }}
              />
              <TextField
                label="Phone Number ID"
                value={enableForm.whatsapp_phone_number_id}
                onChange={(e) => setEnableForm({ ...enableForm, whatsapp_phone_number_id: e.target.value })}
                sx={{ minWidth: 180 }}
              />
            </Stack>
            <Box>
              <ActionButton onClick={enableWhatsApp} disabled={!enableForm.number_id}>
                Enable WhatsApp
              </ActionButton>
            </Box>
          </Stack>
        </FormCard>
      </Stack>

      {/* Send Test WhatsApp slide panel */}
      <SlidePanel
        open={testOpen}
        onClose={() => setTestOpen(false)}
        title="Send Test WhatsApp"
        subtitle="Verify your WhatsApp configuration"
        icon={<WhatsAppIcon />}
        footer={
          <>
            <Button variant="outlined" onClick={() => setTestOpen(false)} sx={{ borderRadius: "10px" }}>
              Close
            </Button>
            <ActionButton onClick={runTest} disabled={!testForm.to || sending}>
              {sending ? "Sending…" : "Send"}
            </ActionButton>
          </>
        }
      >
        <Stack spacing={2}>
          {result && <Alert severity={result.ok ? "success" : "error"}>{result.msg}</Alert>}
          <TextField
            select label="From Number" value={testForm.number_id}
            onChange={(e) => setTestForm({ ...testForm, number_id: e.target.value })} fullWidth
          >
            <MenuItem value="">Auto (first WhatsApp number)</MenuItem>
            {numbers.filter((n: any) => n.whatsapp_enabled).map((n: any) => (
              <MenuItem key={n.id} value={n.id}>{n.number}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="To (E.164)" value={testForm.to}
            onChange={(e) => setTestForm({ ...testForm, to: e.target.value })} fullWidth required
          />
          <TextField
            label="Message" value={testForm.message} multiline minRows={2}
            onChange={(e) => setTestForm({ ...testForm, message: e.target.value })} fullWidth
          />
        </Stack>
      </SlidePanel>
    </AppPage>
  );
};

export default WhatsAppSettings;
